// src/pages/public/Landing.jsx
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../../components/public/Seo";
import "./Landing.css";
import { apiFetch } from "../../lib/api";
import { optimizedBackgroundImage } from "../../lib/optimizedImageUrl";

const heroUrl = `${process.env.PUBLIC_URL}/hero-images/hero.png`;

export default function Landing() {
  const [previewEdits, setPreviewEdits] = React.useState([]);

  React.useEffect(() => {
  async function load() {
    try {
      const data = await apiFetch("/veya-team/edits/public");

// limit to ~5 for landing preview
setPreviewEdits(data.slice(0, 5));
    } catch (e) {
      console.error("Failed to load preview edits", e);
    }
  }

  load();
}, []);

  return (
    <>
      <Seo
        title="Veya — Dress with intention"
        description="A more considered way to discover style, refine what you’re drawn to, and find pieces that align."
      />

      <div className="veya-landing-bg">
  <div className="veya-landing-wrap"></div>
        <div className="veya-landing-wrap">
<header className="veya-landing-header">
  <div className="veya-landing-logo">veya</div>

  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
    <div className="veya-landing-tag">Building in New Zealand</div>

    <Link
      to="/login"
      className="veya-landing-btn veya-landing-btn-secondary"
      style={{
        padding: "6px 14px",
        fontSize: "13px",
        height: "auto",
      }}
    >
      Log in
    </Link>
  </div>
</header>
          <div className="veya-landing-rule" />
        </div>

        <div className="veya-landing-heroWide">
          <section
            className="veya-landing-heroScene"
            aria-label="Veya hero"
            style={{
              backgroundImage: `
                linear-gradient(135deg, rgba(35, 18, 21, 0.14), rgba(91, 31, 43, 0.04)),
                url("${heroUrl}")
              `,
            }}
          >
            <div className="veya-landing-heroCopy">
              <h1 className="veya-landing-metal">Dress with intention.</h1>

              <p className="veya-landing-sub">
                Veya helps you explore style visually, refine what you are drawn
                to, and discover pieces that align — from curated edits to real
                local boutique products.
              </p>

              <p className="veya-landing-sub veya-landing-subtle">
                Designed to help you build a wardrobe that feels like yours.
              </p>

              <div className="veya-landing-actions">
                <Link
                  to="/moods"
                  className="veya-landing-btn veya-landing-btn-primary"
                >
                  Explore Edits
                </Link>

                <Link
                  to="/browse"
                  className="veya-landing-btn veya-landing-btn-secondary"
                >
                  Browse Pieces
                </Link>
              </div>
            </div>
          </section>
        </div>

        <div className="veya-landing-wrap">
          <section className="veya-landing-panel">
            <h2>How Veya works</h2>

            <div className="veya-landing-howGrid">
              <div className="veya-landing-bullet">
                <div className="veya-landing-icon">│</div>
                <div>
                  <strong>Explore edits</strong>
                  <span>
                    Discover visual style directions that help you define what
                    you actually want.
                  </span>
                </div>
              </div>

              <div className="veya-landing-bullet">
                <div className="veya-landing-icon">│</div>
                <div>
                  <strong>Refine your taste</strong>
                  <span>
                    Save looks, notes, and products to build a clearer picture
                    of your personal style.
                  </span>
                </div>
              </div>

              <div className="veya-landing-bullet">
                <div className="veya-landing-icon">│</div>
                <div>
                  <strong>Find matching pieces</strong>
                  <span>
                    Search for products that reflect the colour, silhouette,
                    mood, or styling direction you want.
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className="veya-landing-styles">
            <h2 className="veya-landing-styleTitle">Explore style directions</h2>

            <p className="veya-landing-styleIntro">
              Veya is designed around visual style discovery — from tonal
              layering and soft tailoring to modern romantic dressing, elevated
              basics, and thoughtful outfit building.
            </p>

            <div className="veya-landing-editPreviewGrid vaya-landing-editPreviewGrid--compact">
              {previewEdits.map((edit) => {
                const resolveImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/tryon-assets/")) {
    return `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}${url}`;
  }
  return url;
};

const cover = edit?.images?.[0]
  ? optimizedBackgroundImage(resolveImageUrl(edit.images[0]), {
      width: 520,
      quality: 78,
    })
  : null;

                return (
                  <Link
                    key={edit.slug}
                    to={`/moods/${encodeURIComponent(edit.slug)}`}
                    className="veya-editPreviewCard"
                  >
                    <div
                      className="veya-editPreviewImage"
                      style={cover ? { backgroundImage: cover } : undefined}
                    />
                    <div className="veya-editPreviewContent">
                      <div className="veya-editPreviewTitle">{edit.title}</div>
                      {edit.subtitle ? (
                        <div className="veya-editPreviewSubtitle">
                          {edit.subtitle}
                        </div>
                      ) : null}
                    </div>
                  </Link>
                );
              })}

              <Link to="/moods" className="veya-editPreviewCard veya-editPreviewCard--more">
                <div className="veya-editPreviewMoreInner">
                  <div className="veya-editPreviewMoreEyebrow">Veya’s edits</div>
                  <div className="veya-editPreviewMoreTitle">See more</div>
                </div>
              </Link>
            </div>

            <div className="veya-landing-searchBlock">
              <div className="veya-landing-searchTitle">
                What Veya helps you find
              </div>

              <div className="veya-landing-searchPills">
                <span className="veya-searchPill">boutique fashion</span>
                <span className="veya-searchPill">style edits</span>
                <span className="veya-searchPill">tonal outfits</span>
                <span className="veya-searchPill">modern romantic style</span>
                <span className="veya-searchPill">soft tailoring</span>
                <span className="veya-searchPill">curated wardrobe building</span>
                <span className="veya-searchPill">outfit discovery</span>
                <span className="veya-searchPill">intentional shopping</span>
                <span className="veya-searchPill veya-searchPill--more">and more</span>
              </div>
            </div>
          </section>
        </div>

        <section className="veya-landing-footerBand">
          <div className="veya-landing-wrapInner">
            <div className="veya-landing-ctaWrap">
              <div className="veya-landing-status">EARLY ACCESS COMING SOON</div>

              <div className="veya-landing-footerActions">
                <Link
                  to="/browse"
                  className="veya-landing-btn veya-landing-btn-primary"
                >
                  Start Exploring
                </Link>

                <Link
 to="/merchant-info"
  className="veya-landing-btn veya-landing-btn-secondary"
>
  For Boutiques
</Link>
              </div>
            </div>

<footer className="veya-landing-footerMeta">
  <div className="veya-landing-footerLinks">
    <Link to="/about">About</Link>
    <Link to="/legal">Legal Centre</Link>
    <Link to="/legal/privacy">Privacy Policy</Link>
    <Link to="/legal/terms">Terms of Use</Link>
    <Link to="/legal/merchant-terms">Merchant Terms</Link>
    <Link to="/legal/cookies">Cookies</Link>
    <Link to="/legal/licenses">Licences</Link>
    <a href="mailto:hello@veya.co.nz">Contact</a>
  </div>

  <div className="veya-landing-footerCopyright">
    {new Date().getFullYear()} Veya. All rights reserved.
  </div>
</footer>
          </div>
        </section>
      </div>
    </>
  );
}